import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Boxes, CheckCircle2, AlertCircle, Clock, ShoppingCart, BadgeIndianRupee,
  Wrench, ArrowRight, Plus, Wallet, FileText, Users, ReceiptText,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const KpiCard = ({ label, value, icon: Icon, accent = "text-[#0055FF]", testId, sub, to }) => {
  const inner = (
    <div
      className="p-4 bg-white border border-[#E2E8F0] rounded-lg shadow-sm relative overflow-hidden group hover:-translate-y-0.5 hover:border-[#0055FF]/40 transition-all h-full"
      data-testid={testId}
    >
      <div className="text-[10px] font-mono uppercase tracking-wider text-[#64748B]">{label}</div>
      <div className="text-2xl font-display font-semibold text-[#0A0A0A] tracking-tight mt-1">{value}</div>
      {sub && <div className="text-xs text-[#475569] mt-0.5">{sub}</div>}
      <Icon className={`absolute right-3 top-3 w-5 h-5 ${accent} opacity-20 group-hover:opacity-50 transition-opacity`} />
    </div>
  );
  return to ? <Link to={to} className="block">{inner}</Link> : inner;
};

const QuickAction = ({ to, icon: Icon, title, testId }) => (
  <Link
    to={to}
    className="flex items-center gap-2.5 px-3 py-3 bg-[#0055FF] text-white rounded-lg shadow-sm hover:bg-[#0044dd] transition-colors group"
    data-testid={testId}
  >
    <div className="w-8 h-8 rounded-md bg-white/15 flex items-center justify-center shrink-0">
      <Icon className="w-4 h-4" />
    </div>
    <div className="font-medium text-sm flex items-center gap-1 min-w-0 truncate">{title} <Plus className="w-3.5 h-3.5 opacity-70 shrink-0" /></div>
  </Link>
);

const SubHead = ({ children, to, testId }) => (
  <div className="flex items-center justify-between mb-2">
    <div className="text-[11px] font-mono uppercase tracking-wider text-[#64748B]">{children}</div>
    {to && (
      <Link to={to} className="text-xs text-[#0055FF] font-medium inline-flex items-center gap-1 hover:underline" data-testid={testId}>
        View <ArrowRight className="w-3.5 h-3.5" />
      </Link>
    )}
  </div>
);

const statusPill = (s) =>
  s === "Completed" ? "bg-[#DCFCE7] text-[#166534]"
    : s === "Pending" || s === "Open" ? "bg-[#FEF3C7] text-[#92400E]"
      : "bg-[#EEF2FA] text-[#334155]";

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/dashboard/summary");
        setData(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div className="text-sm text-[#64748B]" data-testid="dashboard-loading">Loading dashboard…</div>;
  if (!data) return null;

  const recent = data.services.recent || [];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex items-end justify-between flex-wrap gap-2">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-wider text-[#64748B] mb-0.5">Overview</div>
          <h1 className="text-2xl md:text-3xl font-display font-semibold tracking-tight">Today at a glance</h1>
        </div>
        <div className="text-xs text-[#64748B] font-mono">{new Date().toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" })}</div>
      </div>

      {/* Quick actions */}
      <section>
        <SubHead>Quick Actions</SubHead>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <QuickAction to="/inventory?new=1" icon={Boxes} title="Add device" testId="quick-add-inventory" />
          <QuickAction to="/inventory" icon={ShoppingCart} title="Record sale" testId="quick-add-sale" />
          <QuickAction to="/services?new=1" icon={Wrench} title="New service" testId="quick-add-service" />
          <QuickAction to="/quotes?new=1" icon={FileText} title="New quote" testId="quick-add-quote" />
          <QuickAction to="/invoices?new=1" icon={ReceiptText} title="New invoice" testId="quick-add-invoice" />
          <QuickAction to="/expenses?new=1" icon={Wallet} title="Add expense" testId="quick-add-expense" />
        </div>
      </section>

      {/* Two-column: left = stacked KPI sections, right = services dashboard */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* LEFT */}
        <div className="lg:col-span-2 space-y-6">
          <section>
            <SubHead to="/inventory" testId="goto-inventory">01 · Inventory — Testing Pipeline</SubHead>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <KpiCard label="Total Stock" value={data.inventory.total} icon={Boxes} to="/inventory" testId="kpi-total-stock" />
              <KpiCard label="Passed" value={data.inventory.passed} icon={CheckCircle2} accent="text-[#22C55E]" to="/inventory" testId="kpi-passed" />
              <KpiCard label="Failed" value={data.inventory.failed} icon={AlertCircle} accent="text-[#EF4444]" to="/inventory" testId="kpi-failed" />
              <KpiCard label="Pending" value={data.inventory.pending} icon={Clock} accent="text-[#F59E0B]" to="/inventory" testId="kpi-pending" />
            </div>
          </section>

          <section>
            <SubHead to="/inventory" testId="goto-sales">02 · Sales — Stock &amp; Revenue</SubHead>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <KpiCard label="Available" value={data.sales.available} icon={Boxes} to="/inventory" testId="kpi-available" />
              <KpiCard label="Sold" value={data.sales.sold} icon={ShoppingCart} to="/inventory" testId="kpi-sold" />
              <KpiCard label="Today's Sales" value={data.sales.today_count} icon={ShoppingCart} accent="text-[#0055FF]" to="/inventory" testId="kpi-today-count" />
              {isAdmin && <KpiCard label="Today's Revenue" value={`₹${Number(data.sales.today_revenue || 0).toLocaleString("en-IN")}`} icon={BadgeIndianRupee} accent="text-[#22C55E]" to="/inventory" testId="kpi-today-revenue" />}
            </div>
          </section>

          <section>
            <SubHead to="/customers" testId="goto-customers">03 · Customers — CRM</SubHead>
            <div className="grid grid-cols-2 gap-3">
              <KpiCard label="Open Leads" value={data.customers.open} icon={Users} accent="text-[#F59E0B]" to="/customers" testId="kpi-leads-open" />
              <KpiCard label="Closed" value={data.customers.closed} icon={CheckCircle2} accent="text-[#22C55E]" to="/customers" testId="kpi-leads-closed" />
            </div>
          </section>

          <section>
            <SubHead to="/invoices" testId="goto-invoices">05 · Tax Invoices — Billing</SubHead>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <KpiCard label="Total Invoices" value={data.invoices?.total ?? 0} icon={ReceiptText} to="/invoices" testId="kpi-invoices-total" />
              <KpiCard label="This Month" value={data.invoices?.month_count ?? 0} icon={FileText} accent="text-[#0055FF]" to="/invoices" testId="kpi-invoices-month" />
              {isAdmin && <KpiCard label="Month Billed" value={`₹${Number(data.invoices?.month_total || 0).toLocaleString("en-IN")}`} icon={BadgeIndianRupee} accent="text-[#22C55E]" to="/invoices" testId="kpi-invoices-month-total" />}
            </div>
          </section>
        </div>

        {/* RIGHT — Services dashboard */}
        <div className="lg:col-span-1">
          <section className="bg-white border border-[#E2E8F0] rounded-lg shadow-sm p-4 h-full">
            <SubHead to="/services" testId="goto-services">04 · Services — Queue</SubHead>
            <div className="grid grid-cols-2 gap-3">
              <KpiCard label="Pending" value={data.services.pending} icon={Clock} accent="text-[#F59E0B]" to="/services" testId="kpi-svc-pending" />
              <KpiCard label="Completed" value={data.services.completed} icon={CheckCircle2} accent="text-[#22C55E]" to="/services" testId="kpi-svc-completed" />
            </div>

            <div className="mt-4">
              <div className="text-[10px] font-mono uppercase tracking-wider text-[#94A3B8] mb-2">Pending Jobs</div>
              {recent.length === 0 ? (
                <div className="text-sm text-[#64748B] py-6 text-center" data-testid="dashboard-recent-empty">No pending service jobs.</div>
              ) : (
                <div className="space-y-1.5" data-testid="dashboard-recent-services">
                  {recent.map((s) => (
                    <Link
                      to="/services"
                      key={s.service_id}
                      className="flex items-center gap-2 p-2 rounded-md hover:bg-slate-50 transition-colors group"
                      data-testid={`dashboard-service-${s.service_id}`}
                    >
                      <div className="w-8 h-8 rounded-md bg-[#EEF2FA] flex items-center justify-center shrink-0">
                        <Wrench className="w-4 h-4 text-[#0055FF]" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium truncate">{[s.brand, s.model_name].filter(Boolean).join(" ") || s.device_type || "Device"}</div>
                        <div className="text-[11px] text-[#64748B] truncate font-mono">{s.service_id} · {s.customer_name}</div>
                      </div>
                      <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded shrink-0 ${statusPill(s.status)}`}>{s.status}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
